import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, ShieldCheck, ChevronRight, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function Login() {
  const { user, sendOtp, verifyOtp } = useAuth();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("phone"); // phone | otp
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [debugOtp, setDebugOtp] = useState("");
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const handleSend = async (e) => {
    e?.preventDefault();
    if (phone.length < 10) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    setSending(true);
    try {
      const data = await sendOtp(phone);
      setDebugOtp(data.otp_debug || "");
      setStep("otp");
      if (data.otp_debug) {
        toast.success(`OTP sent to +${phone}`, {
          description: `Demo OTP: ${data.otp_debug}`,
        });
      } else {
        toast.success(`OTP sent via SMS to +${phone}`, {
          description: "Check your messages — code is valid for 5 minutes.",
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (code) => {
    if (code.length !== 6) return;
    setVerifying(true);
    try {
      await verifyOtp(phone, code);
      toast.success("Welcome back to Udyogg");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
      setOtp("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-zinc-100">
      {/* Left panel — brand + info */}
      <div className="hidden lg:flex lg:col-span-3 relative bg-white text-zinc-900 flex-col p-12 overflow-hidden border-r-2 border-zinc-900">
        <div className="caution-stripe absolute top-0 left-0 right-0 h-2" />
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-[#FF5A00] flex items-center justify-center border-2 border-zinc-900">
            <Zap className="w-6 h-6 text-zinc-900" strokeWidth={2.5} />
          </div>
          <div className="font-display text-3xl font-black tracking-tight">
            UDYO<span className="text-[#FF5A00]">GG</span>
          </div>
        </div>

        <div className="mt-24 max-w-lg">
          <div className="tag text-[#FF5A00] mb-4">INDUSTRIAL COMMERCE PLATFORM</div>
          <h1 className="font-display text-5xl xl:text-6xl font-black leading-[1.02] tracking-tight">
            Live inventory.<br />
            Live prices.<br />
            <span className="text-[#FF5A00]">10-minute quotes.</span>
          </h1>
          <p className="mt-6 text-zinc-500 text-lg font-light max-w-md">
            Bulk polymers, chemicals and CPW oils delivered to your factory.
            Track every drum in real-time.
          </p>
        </div>

        <div className="mt-auto grid grid-cols-3 gap-6 border-t-2 border-zinc-900 pt-8">
          <div>
            <div className="font-mono-data text-3xl font-bold text-[#FF5A00]">2,400+</div>
            <div className="tag mt-1">Live SKUs</div>
          </div>
          <div>
            <div className="font-mono-data text-3xl font-bold text-[#FF5A00]">18 min</div>
            <div className="tag mt-1">Avg dispatch</div>
          </div>
          <div>
            <div className="font-mono-data text-3xl font-bold text-[#FF5A00]">99.4%</div>
            <div className="tag mt-1">Purity assured</div>
          </div>
        </div>
      </div>

      {/* Right panel — auth form */}
      <div className="lg:col-span-2 flex items-center p-6 lg:p-12 grid-bg">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-[#FF5A00] flex items-center justify-center border-2 border-zinc-900">
              <Zap className="w-5 h-5 text-zinc-900" strokeWidth={2.5} />
            </div>
            <div className="font-display text-2xl font-black tracking-tight">
              UDYO<span className="text-[#FF5A00]">GG</span>
            </div>
          </div>

          <div className="bg-white border-2 border-zinc-900 p-8 hard-shadow-sm">
            <div className="tag">
              {step === "phone" ? "STEP 01 / 02" : "STEP 02 / 02"}
            </div>
            <h2 className="font-display text-3xl font-black mt-2 tracking-tight">
              {step === "phone" ? "Sign in with mobile" : "Enter OTP"}
            </h2>
            <p className="text-zinc-500 text-sm mt-2">
              {step === "phone"
                ? "We'll send a 6-digit code to verify."
                : `Sent to +${phone}. Valid for 5 minutes.`}
            </p>

            {step === "phone" ? (
              <form onSubmit={handleSend} className="mt-8 space-y-5">
                <div>
                  <label className="tag block mb-2">Mobile number</label>
                  <div className="flex border-2 border-zinc-900 focus-within:border-[#FF5A00]">
                    <span className="bg-zinc-900 text-white px-3 flex items-center font-mono-data text-sm">
                      +91
                    </span>
                    <input
                      data-testid="phone-input"
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) =>
                        setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      placeholder="9876543210"
                      className="flex-1 outline-none px-4 py-3 font-mono-data text-lg"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending || phone.length < 10}
                  data-testid="send-otp-button"
                  className="w-full bg-[#FF5A00] text-white font-mono-data tracking-widest text-sm py-4 border-2 border-zinc-900 hard-shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sending ? "SENDING..." : (
                    <>SEND OTP <ChevronRight className="w-4 h-4" /></>
                  )}
                </button>

                <div className="text-center pt-2 border-t border-zinc-200">
                  <p className="text-sm text-zinc-500 mt-4">
                    New here?{" "}
                    <Link
                      to="/register"
                      data-testid="go-to-register-link"
                      className="font-semibold text-[#FF5A00] hover:underline"
                    >
                      Register your firm →
                    </Link>
                  </p>
                </div>
              </form>
            ) : (
              <div className="mt-8 space-y-5">
                <div>
                  <label className="tag block mb-3">6-digit code</label>
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={(v) => {
                      setOtp(v);
                      if (v.length === 6) handleVerify(v);
                    }}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot
                          key={i}
                          index={i}
                          className="w-12 h-14 text-xl font-mono-data font-bold border-2 border-zinc-900 rounded-none"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {debugOtp && (
                  <div
                    data-testid="debug-otp-banner"
                    className="bg-amber-50 border-2 border-amber-400 px-3 py-2 font-mono-data text-xs flex items-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4 text-amber-700" />
                    <span>DEMO OTP: <strong>{debugOtp}</strong></span>
                  </div>
                )}

                <button
                  onClick={() => handleVerify(otp)}
                  disabled={verifying || otp.length !== 6}
                  data-testid="verify-otp-button"
                  className="w-full bg-zinc-900 text-white font-mono-data tracking-widest text-sm py-4 border-2 border-zinc-900 hard-shadow-sm disabled:opacity-50"
                >
                  {verifying ? "VERIFYING..." : "VERIFY & CONTINUE"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep("phone");
                    setOtp("");
                    setDebugOtp("");
                  }}
                  data-testid="change-number-button"
                  className="w-full text-sm text-zinc-600 hover:text-zinc-900 underline underline-offset-4"
                >
                  Change number
                </button>
              </div>
            )}
          </div>

          <p className="mt-6 text-xs text-zinc-500 font-mono-data">
            By continuing you agree to Udyogg's terms & privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
}
